import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { PageHeader } from "@/components/ui-kit";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sparkles, ShoppingCart, ReceiptText, ArrowDownCircle, ArrowUpCircle, Receipt, Users, Landmark, Package, ClipboardList } from "lucide-react";
import { toast } from "sonner";

const SOURCES = [
  { value: "ventes", label: "Ventes (factures)", icon: ReceiptText, desc: "D 411 Clients / C 701 Ventes + C 443 TVA collectée" },
  { value: "achats", label: "Achats (fournisseurs)", icon: ShoppingCart, desc: "D 601 Achats / C 401 Fournisseurs" },
  { value: "encaissements", label: "Encaissements clients", icon: ArrowDownCircle, desc: "D 521/571 Trésorerie / C 411 Clients" },
  { value: "decaissements", label: "Décaissements", icon: ArrowUpCircle, desc: "D 401/421/447 / C 521/571 Trésorerie" },
  { value: "charges", label: "Charges", icon: Receipt, desc: "D 605 Charges / C 401 Fournisseurs" },
  { value: "salaires", label: "Salaires (bulletins)", icon: Users, desc: "D 661 Salaires / C 431 CNSS + C 421 Net" },
  { value: "taxes", label: "Taxes & impôts", icon: Landmark, desc: "D 641 Impôts / C 447 État" },
  { value: "stock", label: "Mouvements de stock", icon: Package, desc: "D 311 Stocks / C 601 (Entrée) — inverse en Sortie" },
  { value: "inventaire", label: "Inventaires", icon: ClipboardList, desc: "Écart d'inventaire : D/C 311 Stocks ↔ 601" },
];

export default function AutoGeneration() {
  const [periods, setPeriods] = useState([]);
  const [pid, setPid] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [busy, setBusy] = useState("");
  const [results, setResults] = useState({});

  useEffect(() => { api.get("/accounting/periods").then((r) => { setPeriods(r.data); const a = r.data.find((x) => x.active) || r.data[0]; setPid(a?.id || ""); if (a) { setStart(a.date_debut); setEnd(a.date_fin); } }).catch(() => {}); }, []);

  const generate = async (source) => {
    setBusy(source);
    try {
      const { data } = await api.post("/accounting/generate", { source, start, end, period_id: pid });
      setResults((r) => ({ ...r, [source]: data }));
      toast.success(data.message);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(""); }
  };

  return (
    <div>
      <PageHeader title="Génération automatique des écritures" subtitle="Depuis Ventes, Achats, Paiements, Charges, Salaires & Taxes — écritures créées en brouillon puis à valider" />

      <div className="rounded-xl border bg-white p-4 mb-4 flex flex-wrap items-end gap-3">
        <div><label className="text-[11px] uppercase text-slate-400">Exercice</label>
          <Select value={pid} onValueChange={setPid}><SelectTrigger className="w-52 mt-1" data-testid="gen-period"><SelectValue /></SelectTrigger>
            <SelectContent>{periods.map((p) => <SelectItem key={p.id} value={p.id}>{p.libelle}</SelectItem>)}</SelectContent></Select></div>
        <div><label className="text-[11px] uppercase text-slate-400">Du</label><Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="mt-1" data-testid="gen-start" /></div>
        <div><label className="text-[11px] uppercase text-slate-400">Au</label><Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="mt-1" data-testid="gen-end" /></div>
        <p className="text-sm text-slate-400 flex-1 min-w-full sm:min-w-0">Les écritures existantes ne sont jamais dupliquées. Elles apparaissent en <b>brouillon</b> dans « Écritures » où vous pouvez les vérifier et les valider.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {SOURCES.map((s) => {
          const res = results[s.value];
          return (
            <div key={s.value} className="rounded-xl border bg-white p-4 flex flex-col" data-testid={`gen-card-${s.value}`}>
              <div className="flex items-center gap-2 mb-1"><s.icon className="h-5 w-5 text-brand-600" /><span className="font-semibold">{s.label}</span></div>
              <p className="text-xs text-slate-400 font-mono mb-3 flex-1">{s.desc}</p>
              {res && <p className="text-xs mb-2 text-emerald-700">{res.created} créée(s) · {res.skipped} ignorée(s)</p>}
              <Button onClick={() => generate(s.value)} disabled={busy === s.value} data-testid={`gen-btn-${s.value}`} className="bg-brand-600 hover:bg-brand-700 text-white"><Sparkles className="h-4 w-4 mr-1.5" />{busy === s.value ? "Génération…" : "Générer les écritures"}</Button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
